export const fragmentShader = `
precision highp float;
precision highp int;

// (sqrt(5) - 1)/4 = F4, used once below
#define F4 0.309016994374947451
#define PI 3.14159

uniform float time;
uniform float permutations;
uniform float iterations;
uniform vec2 uvScale;
uniform vec3 color1;
uniform vec3 color2;
uniform vec3 color3;
uniform float brightness;
uniform float speed;

varying vec2 vUv;

// Description : Array and textureless GLSL 2D/3D/4D simplex
//               noise functions.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : ijm
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//

vec4 mod289(vec4 x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}

float mod289(float x) {
    return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
    return mod289(((x*34.0)+1.0)*x);
}

float permute(float x) {
    return mod289(((x*34.0)+1.0)*x);
}

vec4 taylorInvSqrt(vec4 r) {
    return 1.79284291400159 - 0.85373472095314 * r;
}

float taylorInvSqrt(float r) {
    return 1.79284291400159 - 0.85373472095314 * r;
}

vec4 grad4(float j, vec4 ip) {
    const vec4 ones = vec4(1.0, 1.0, 1.0, -1.0);
    vec4 p,s;

    p.xyz = floor( fract (vec3(j) * ip.xyz) * 7.0) * ip.z - 1.0;
    p.w = 1.5 - dot(abs(p.xyz), ones.xyz);
    s = vec4(lessThan(p, vec4(0.0)));
    p.xyz = p.xyz + (s.xyz*2.0 - 1.0) * s.www;

    return p;
}

float snoise(vec4 v) {
    const vec4  C = vec4( 0.138196601125011,  // (5 - sqrt(5))/20  G4
                    0.276393202250021,  // 2 * G4
                    0.414589803375032,  // 3 * G4
                    -0.447213595499958); // -1 + 4 * G4

    // First corner
    vec4 i  = floor(v + dot(v, vec4(F4)) );
    vec4 x0 = v -   i + dot(i, C.xxxx);

    // Other corners

    // Rank sorting originally contributed by Bill Licea-Kane, AMD (formerly ATI)
    vec4 i0;
    vec3 isX = step( x0.yzw, x0.xxx );
    vec3 isYZ = step( x0.zww, x0.yyz );
    //  i0.x = dot( isX, vec3( 1.0 ) );
    i0.x = isX.x + isX.y + isX.z;
    i0.yzw = 1.0 - isX;
    //  i0.y += dot( isYZ.xy, vec2( 1.0 ) );
    i0.y += isYZ.x + isYZ.y;
    i0.zw += 1.0 - isYZ.xy;
    i0.z += isYZ.z;
    i0.w += 1.0 - isYZ.z;

    // i0 now contains the unique values 0,1,2,3 in each channel
    vec4 i3 = clamp( i0, 0.0, 1.0 );
    vec4 i2 = clamp( i0-1.0, 0.0, 1.0 );
    vec4 i1 = clamp( i0-2.0, 0.0, 1.0 );

    //  x0 = x0 - 0.0 + 0.0 * C.xxxx
    //  x1 = x0 - i1  + 1.0 * C.xxxx
    //  x2 = x0 - i2  + 2.0 * C.xxxx
    //  x3 = x0 - i3  + 3.0 * C.xxxx
    //  x4 = x0 - 1.0 + 4.0 * C.xxxx
    vec4 x1 = x0 - i1 + C.xxxx;
    vec4 x2 = x0 - i2 + C.yyyy;
    vec4 x3 = x0 - i3 + C.zzzz;
    vec4 x4 = x0 + C.wwww;

    // Permutations
    i = mod289(i);
    float j0 = permute( permute( permute( permute(i.w) + i.z) + i.y) + i.x);
    vec4 j1 = permute( permute( permute( permute (
                                            i.w + vec4(i1.w, i2.w, i3.w, 1.0 ))
                                    + i.z + vec4(i1.z, i2.z, i3.z, 1.0 ))
                            + i.y + vec4(i1.y, i2.y, i3.y, 1.0 ))
                    + i.x + vec4(i1.x, i2.x, i3.x, 1.0 ));

    // Gradients: 7x7x6 points over a cube, mapped onto a 4-cross polytope
    // 7*7*6 = 294, which is close to the ring size 17*17 = 289.
    vec4 ip = vec4(1.0/294.0, 1.0/49.0, 1.0/7.0, 0.0) ;

    vec4 p0 = grad4(j0,   ip);
    vec4 p1 = grad4(j1.x, ip);
    vec4 p2 = grad4(j1.y, ip);
    vec4 p3 = grad4(j1.z, ip);
    vec4 p4 = grad4(j1.w, ip);

    // Normalise gradients
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;
    p4 *= taylorInvSqrt(dot(p4,p4));

    // Mix contributions from the five corners
    vec3 m0 = max(0.6 - vec3(dot(x0,x0), dot(x1,x1), dot(x2,x2)), 0.0);
    vec2 m1 = max(0.6 - vec2(dot(x3,x3), dot(x4,x4)            ), 0.0);
    m0 = m0 * m0;
    m1 = m1 * m1;
    return (
            49.0 * ( dot(m0*m0, vec3( dot( p0, x0 ), dot( p1, x1 ), dot( p2, x2 )))
                    + dot(m1*m1, vec2( dot( p3, x3 ), dot( p4, x4 ) ) ) )
    );

}

// makes a pseudorandom number between 0 and 1
float hash(float n) {
        return fract(sin(n)*93942.234);
}

// rotation matrix
mat2 m = mat2(0.6,0.8,-0.8,0.6);

// fractional brownian motion (i.e. photoshop clouds)
float fbm(vec4 p) {
    float f = 0.0;
    f += 0.5 * snoise(vec4( p.xy * m, p.zw * m ));
    p *= 2.02;
    f += 0.25 * snoise(vec4( p.xy * m, p.zw * m ));
    p *= 2.01;
    f += 0.125 * snoise(vec4( p.xy * m, p.zw * m ));
    p *= 2.03;
    f += 0.0625 * snoise(vec4( p.xy * m, p.zw * m ));
    f /= 0.9375;
    return f;
}

void main() {
    // relative coordinates
    vec2 p = vUv * uvScale;
    float elapsed = time * speed * 0.01;

    float s = vUv.x * uvScale.x;
    float t = vUv.y * uvScale.y;

    // Tiling 4d noise based on
    // https://gamedev.stackexchange.com/questions/23625/how-do-you-generate-tileable-perlin-noise/23639#23639
    float multiplier = iterations / ( 2.0 * PI );
    float nx = cos( s * 2.0 * PI ) * multiplier;
    float ny = cos( t * 2.0 * PI ) * multiplier;
    float nz = sin( s * 2.0 * PI ) * multiplier;
    float nw = sin( t * 2.0 * PI ) * multiplier;

    vec4 tile4d = vec4( nx, ny, nz, nw );

    vec2 a = vec2(
            fbm( tile4d + elapsed * 1.1 ),
            fbm( tile4d - elapsed * 1.3 )
    );

    vec2 b = vec2(
            fbm( tile4d + elapsed * 1.2 + a.x * 2.0 ),
            fbm( tile4d - elapsed * 1.2 + a.x * 3.0 )
    );

    float surf = fbm( tile4d + elapsed + length( b ) * permutations );

    // mix in some color
    vec3 colorOutput = brightness * (
            ( ( b.x + surf ) * color1 ) +
            ( ( b.y + surf ) * color2 ) +
            ( ( surf + b.x ) * color3 )
    );

    gl_FragColor = vec4( colorOutput, 1.);
}
`
export const vertexShader = `
/**
 * Example Vertex Shader
 * Sets the position of the vertex by setting gl_Position
 */

 // Set the precision for data types used in this shader
 precision mediump float;
 precision mediump int;

 // Default THREE.js uniforms available to both fragment and vertex shader
 uniform mat4 modelMatrix;
 uniform mat4 modelViewMatrix;
 uniform mat4 projectionMatrix;
 uniform mat4 viewMatrix;
 uniform mat3 normalMatrix;

 // Default uniforms provided by ShaderFrog.
 uniform vec3 cameraPosition;
 uniform float time;

 // Default attributes provided by THREE.js. Attributes are only available in the
 // vertex shader. You can pass them to the fragment shader using varyings
 attribute vec3 position;
 attribute vec3 normal;
 attribute vec2 uv;
 attribute vec2 uv2;

 // Examples of variables passed from vertex to fragment shader
 varying vec3 vPosition;
 varying vec3 vNormal;
 varying vec2 vUv;
 varying vec2 vUv2;

 void main() {

     // To pass variables to the fragment shader, you assign them here in the
     // main function. Traditionally you name the varying with vAttributeName
     vNormal = normal;
     vUv = uv;
     vUv2 = uv2;
     vPosition = position;

     // This sets the position of the vertex in 3d space. The correct math is
     // provided below to take into account camera and object data.
     gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );

 }
`